import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence, useInView } from "framer-motion";
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider";
import axios from "axios";
import { toast } from "sonner";
import { 
  Car, Shield, Sparkles, Clock, MapPin, Phone, Mail, 
  ChevronRight, Star, Menu, X, CheckCircle2, ArrowRight,
  Droplets, Sun, Thermometer, MessageSquare, Send, FileText, AlertCircle,
  ChevronDown, User, ChevronLeft
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth, API } from "../App";
import BlogStripSection from "../components/BlogStripSection";
import InstagramFeedSection from "../components/InstagramFeedSection";
import PromotionBanner from "../components/PromotionBanner";
import PartnerLoginTrigger from "../components/PartnerLoginTrigger";
import { yearsInBusiness } from "../constants/business";

const PHONE_NUMBER = "+353858237373";
const WHATSAPP_LINK = `https://wa.me/353858237373?text=Hi%20SaturnStyling%2C%20I%27m%20interested%20in%20your%20services`;

// Contact Options Popup Component
const ContactPopup = ({ isOpen, onClose, title = "Get in Touch" }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-saturn-slate border-slate-700 text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-orbitron text-xl text-white flex items-center gap-2">
            <Phone className="w-5 h-5 text-saturn-orange" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Choose how you'd like to reach us
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col gap-3 mt-4">
          <a href={`tel:${PHONE_NUMBER}`} className="w-full">
            <Button
              className="w-full bg-saturn-orange hover:bg-saturn-orange-hover text-white font-rajdhani font-bold uppercase tracking-wider py-4 flex items-center justify-center gap-3"
              data-testid="popup-call-btn"
            >
              <Phone className="w-5 h-5" />
              Call Now
              <span className="text-sm font-normal opacity-80">085 823 7373</span>
            </Button>
          </a>
          
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="w-full">
            <Button
              className="w-full bg-green-600 hover:bg-green-700 text-white font-rajdhani font-bold uppercase tracking-wider py-4 flex items-center justify-center gap-3"
              data-testid="popup-whatsapp-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp Us
            </Button>
          </a>
        </div>
        
        <p className="text-center text-slate-500 text-xs mt-4">
          Available Mon-Sat, 10am - 6pm
        </p>
      </DialogContent>
    </Dialog>
  );
};

// Floating Contact Button Component
const FloatingContactButton = ({ onClick }) => {
  return (
    <motion.button
      onClick={onClick}
      className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-green-600 hover:bg-green-700 text-white shadow-lg flex items-center justify-center"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 1, type: "spring" }}
      data-testid="floating-contact-btn"
    >
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      <span className="absolute -top-1 -right-1 w-3 h-3 bg-saturn-orange rounded-full animate-ping" />
      <span className="absolute -top-1 -right-1 w-3 h-3 bg-saturn-orange rounded-full" />
    </motion.button>
  );
};

// Live Google Rating Card — fetches /api/reputation/google server-side once
// per page load and displays the aggregate rating + review count with a
// direct link to the Google Maps listing. Falls back to a "Rated on Google"
// chip if the endpoint isn't configured yet (503) or errors out.
const GoogleRatingCard = () => {
  const [state, setState] = useState({ status: "loading", data: null });
  const years = yearsInBusiness();

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/reputation/google`)
      .then((res) => {
        if (!cancelled) setState({ status: "ok", data: res.data });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error", data: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rating = state.data?.rating;
  const count = state.data?.review_count;
  const mapsUrl =
    state.data?.google_maps_url ||
    "https://www.google.com/search?q=Saturn+Styling+Duleek+reviews";
  const ratingLabel =
    typeof rating === "number" ? rating.toFixed(1) : null;

  return (
    <motion.div
      className="glass rounded-xl p-4"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      data-testid="google-rating-card"
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-saturn-orange flex items-center justify-center flex-shrink-0">
          <Star className="w-6 h-6 text-white" fill="currentColor" />
        </div>
        <div className="flex-1 min-w-0">
          {state.status === "loading" ? (
            <>
              <div className="h-4 w-32 bg-slate-700/60 rounded animate-pulse mb-2" />
              <div className="h-3 w-40 bg-slate-700/40 rounded animate-pulse" />
            </>
          ) : ratingLabel ? (
            <>
              <div
                className="font-rajdhani font-bold text-white text-lg flex items-baseline gap-1"
                data-testid="google-rating-value"
              >
                <span>{ratingLabel}</span>
                <span className="text-slate-400 text-sm font-normal">/ 5</span>
                {typeof count === "number" && (
                  <span
                    className="text-slate-400 text-sm font-normal ml-1"
                    data-testid="google-rating-count"
                  >
                    · {count.toLocaleString()} Google reviews
                  </span>
                )}
              </div>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-saturn-orange hover:text-saturn-orange-hover underline underline-offset-2"
                data-testid="google-rating-link"
              >
                Read reviews on Google →
              </a>
            </>
          ) : (
            <>
              <div className="font-rajdhani font-bold text-white">
                Rated on Google
              </div>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-saturn-orange hover:text-saturn-orange-hover underline underline-offset-2"
                data-testid="google-rating-link"
              >
                See Google reviews →
              </a>
            </>
          )}
          <div
            className="text-xs text-slate-400 mt-1"
            data-testid="years-in-business-badge"
          >
            {years}+ years in business · Meath &amp; Dublin
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const MAX_REVIEW_CHARS = 200;

// Live Google 5-star review carousel — pulls the 3 most recent 5-star
// reviews from GET /api/reputation/google and rotates through them every
// 6s. Manual arrows are exposed via data-testid for tests. Google
// attribution rules: we MUST display the author name, star row, publish
// time, and link to the review — the API response provides all of them.
const ReviewCarousel = () => {
  const [reviews, setReviews] = useState(null); // null = loading, [] = failed/none
  const [google_maps_url, setGoogleMapsUrl] = useState("");
  const [active, setActive] = useState(0);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/reputation/google`)
      .then((res) => {
        if (cancelled) return;
        setReviews(res.data?.reviews || []);
        setGoogleMapsUrl(res.data?.google_maps_url || "");
      })
      .catch(() => {
        if (!cancelled) setReviews([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-rotate every 6s once we have 2+ reviews.
  useEffect(() => {
    if (!reviews || reviews.length < 2) return undefined;
    const t = setInterval(() => {
      setActive((prev) => (prev + 1) % reviews.length);
    }, 6000);
    return () => clearInterval(t);
  }, [reviews]);

  // Hide the whole section when unconfigured / no reviews returned.
  if (!reviews || reviews.length === 0) return null;

  const go = (delta) => {
    setActive((prev) => (prev + delta + reviews.length) % reviews.length);
  };
  const current = reviews[active];

  return (
    <section
      id="reviews"
      className="py-16 md:py-24 relative overflow-hidden"
      data-testid="review-carousel-section"
    >
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal direction="up">
          <div className="text-center mb-10">
            <span className="text-saturn-orange font-rajdhani uppercase tracking-widest text-sm">
              Real Words · Recent 5-star reviews
            </span>
            <h2 className="font-orbitron text-3xl md:text-4xl font-bold tracking-tight uppercase text-white mt-3">
              Straight from Google
            </h2>
          </div>
        </ScrollReveal>

        <div
          className="relative glass rounded-2xl p-6 md:p-10 min-h-[240px]"
          data-testid="review-carousel"
        >
          {/* Arrows */}
          {reviews.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => go(-1)}
                aria-label="Previous review"
                data-testid="review-carousel-prev"
                className="absolute left-2 md:-left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-slate-800/80 hover:bg-saturn-orange text-white flex items-center justify-center transition-colors z-10"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                aria-label="Next review"
                data-testid="review-carousel-next"
                className="absolute right-2 md:-right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-slate-800/80 hover:bg-saturn-orange text-white flex items-center justify-center transition-colors z-10"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.35 }}
              data-testid={`review-item-${active}`}
              className="px-6 md:px-8"
            >
              {/* Star row */}
              <div
                className="flex gap-1 mb-4 justify-center"
                aria-label={`${current.rating} out of 5 stars`}
              >
                {Array.from({ length: current.rating || 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="w-5 h-5 text-saturn-orange"
                    fill="currentColor"
                  />
                ))}
              </div>

              {/* Quote */}
              {(() => {
                const fullText = current.text || "";
                const isLong = fullText.length > MAX_REVIEW_CHARS;
                const displayText = isLong
                  ? fullText.slice(0, MAX_REVIEW_CHARS).trimEnd() + "…"
                  : fullText;
                return (
                  <p
                    className="text-slate-200 font-inter text-lg md:text-xl leading-relaxed text-center italic"
                    data-testid={`review-quote-${active}`}
                  >
                    &ldquo;{displayText}&rdquo;
                    {isLong && (
                      <>
                        {" "}
                        <a
                          href={current.review_url || google_maps_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="not-italic text-sm text-saturn-orange hover:underline whitespace-nowrap"
                          data-testid={`review-expander-${active}`}
                        >
                          Read full review on Google →
                        </a>
                      </>
                    )}
                  </p>
                );
              })()}

              {/* Attribution — required by Google Places TOS */}
              <div className="mt-6 flex items-center justify-center gap-3">
                {current.author_photo && (
                  <img
                    src={current.author_photo}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                )}
                <div className="text-left">
                  <div
                    className="font-rajdhani font-semibold text-white"
                    data-testid={`review-author-${active}`}
                  >
                    {current.author_name || "Verified Google reviewer"}
                  </div>
                  <div className="text-xs text-slate-400 flex items-center gap-2">
                    <span>{current.relative_time || ""}</span>
                    <span>·</span>
                    <a
                      href={current.review_url || google_maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-saturn-orange hover:underline"
                    >
                      View on Google
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Dots */}
          {reviews.length > 1 && (
            <div className="mt-6 flex gap-2 justify-center">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActive(i)}
                  aria-label={`Show review ${i + 1}`}
                  data-testid={`review-carousel-dot-${i}`}
                  className={`h-2 rounded-full transition-all ${
                    i === active
                      ? "w-8 bg-saturn-orange"
                      : "w-2 bg-slate-600 hover:bg-slate-400"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="text-center mt-6">
          <a
            href={google_maps_url || "https://www.google.com/search?q=Saturn+Styling+Duleek+reviews"}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-saturn-orange hover:text-saturn-orange-hover underline underline-offset-2"
            data-testid="review-carousel-all-link"
          >
            See all reviews on Google →
          </a>
        </div>
      </div>
    </section>
  );
};

// Starfield Background Component
const Starfield = () => {
  const stars = Array.from({ length: 100 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 2 + 1,
    delay: Math.random() * 3,
  }));

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {stars.map((star) => (
        <div
          key={star.id}
          className="absolute rounded-full bg-white star"
          style={{
            left: `${star.x}%`,
            top: `${star.y}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            opacity: 0.3,
            animationDelay: `${star.delay}s`,
          }}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-radial from-indigo-950/20 via-transparent to-transparent" />
    </div>
  );
};

// Quote Request Modal Component
const QuoteRequestModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    service_type: "full_wrap",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    vehicle_size: "estate",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Bot traps (SEC-N1) — invisible to real customers. `hpCompany` is a
  // visually-hidden input only a form-filling script populates;
  // `openedAt` measures how long the form was on screen.
  const [hpCompany, setHpCompany] = useState("");
  const openedAt = React.useRef(Date.now());

  useEffect(() => {
    if (isOpen) openedAt.current = Date.now();
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/services/quote-request`, {
        ...formData,
        hp_company: hpCompany,
        form_elapsed_ms: Date.now() - openedAt.current,
      });
      setSubmitted(true);
      toast.success("Quote request submitted! We'll contact you within 24 hours.");
      setTimeout(() => {
        onClose();
        setSubmitted(false);
        setHpCompany("");
        setFormData({
          service_type: "full_wrap",
          vehicle_make: "",
          vehicle_model: "",
          vehicle_year: "",
          vehicle_size: "estate",
          customer_name: "",
          customer_email: "",
          customer_phone: "",
          description: "",
        });
      }, 2000);
    } catch (error) {
      toast.error("Failed to submit quote request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-saturn-slate border-slate-700 text-white max-w-lg">
          <div className="flex flex-col items-center justify-center py-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-4"
            >
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </motion.div>
            <h3 className="font-orbitron text-xl font-bold text-white mb-2">Quote Requested!</h3>
            <p className="text-slate-400 text-center">We'll contact you within 24 hours with a custom quote.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-saturn-slate border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-orbitron text-2xl text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-saturn-orange" />
            Request a Quote
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            For Estate, Jeep/MPV full wraps or custom jobs, we provide personalized quotes.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Honeypot — visually hidden, never focusable by a real user.
              Only automated form-fillers populate this (SEC-N1). */}
          <div aria-hidden="true" className="absolute left-[-9999px] top-auto w-px h-px overflow-hidden">
            <label htmlFor="quote-company-website">Company website</label>
            <input
              id="quote-company-website"
              name="company_website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={hpCompany}
              onChange={(e) => setHpCompany(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Service Type</Label>
            <Select
              value={formData.service_type}
              onValueChange={(v) => setFormData({ ...formData, service_type: v })}
            >
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="full_wrap">Full Vehicle Wrap</SelectItem>
                <SelectItem value="partial_wrap">Partial Wrap</SelectItem>
                <SelectItem value="custom_tint">Custom Tinting Job</SelectItem>
                <SelectItem value="fleet">Fleet Service</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Vehicle Make</Label>
              <Input
                value={formData.vehicle_make}
                onChange={(e) => setFormData({ ...formData, vehicle_make: e.target.value })}
                placeholder="e.g., BMW, Audi"
                className="bg-slate-800 border-slate-700 text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Vehicle Model</Label>
              <Input
                value={formData.vehicle_model}
                onChange={(e) => setFormData({ ...formData, vehicle_model: e.target.value })}
                placeholder="e.g., X5, Q7"
                className="bg-slate-800 border-slate-700 text-white"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Year</Label>
              <Input
                value={formData.vehicle_year}
                onChange={(e) => setFormData({ ...formData, vehicle_year: e.target.value })}
                placeholder="e.g., 2024"
                className="bg-slate-800 border-slate-700 text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Vehicle Size</Label>
              <Select
                value={formData.vehicle_size}
                onValueChange={(v) => setFormData({ ...formData, vehicle_size: v })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="hatch">Hatch</SelectItem>
                  <SelectItem value="sedan">Sedan</SelectItem>
                  <SelectItem value="estate">Estate</SelectItem>
                  <SelectItem value="suv">Jeep/MPV/SUV</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Your Name</Label>
            <Input
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              placeholder="Full name"
              className="bg-slate-800 border-slate-700 text-white"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                placeholder="your@email.com"
                className="bg-slate-800 border-slate-700 text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Phone</Label>
              <Input
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                placeholder="+353..."
                className="bg-slate-800 border-slate-700 text-white"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Additional Details (Optional)</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Tell us more about your project - colors, specific requirements, etc."
              className="bg-slate-800 border-slate-700 text-white min-h-[100px]"
              data-testid="quote-description-input"
            />
            <p className="text-xs text-slate-500">
              Please don't include personal details like plate numbers or addresses — we don't need them at this stage.
            </p>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-saturn-orange hover:bg-saturn-orange-hover text-white font-rajdhani font-bold uppercase tracking-wider py-3"
            data-testid="submit-quote-btn"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1 }}
                  className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                />
                Submitting...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Submit Quote Request
              </span>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Scroll Animation Wrapper Component
const ScrollReveal = ({ children, delay = 0, direction = "up" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const variants = {
    hidden: {
      opacity: 0,
      y: direction === "up" ? 50 : direction === "down" ? -50 : 0,
      x: direction === "left" ? 50 : direction === "right" ? -50 : 0,
    },
    visible: {
      opacity: 1,
      y: 0,
      x: 0,
    },
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

// Parallax Image Component
const ParallaxImage = ({ src, alt, className }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-50, 50]);

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y }}
        className="w-full h-full object-cover scale-110"
      />
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
    <nav
      style={{ top: "var(--promo-banner-h, 0px)" }}
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-2" : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <div className="w-12 h-12 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/zdb6hh5n_Eima%20logo%20Speed%20rate%20font%2020x20.png"
                alt="Saturn Styling"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="font-orbitron font-bold text-xl text-white hidden sm:block">
              SATURNSTYLING
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-slate-300 hover:text-saturn-orange transition-colors font-rajdhani">
              Services
            </a>
            <Link to="/blog" className="text-slate-300 hover:text-saturn-orange transition-colors font-rajdhani" data-testid="nav-blog-link">
              Guides
            </Link>
            <a href="#about" className="text-slate-300 hover:text-saturn-orange transition-colors font-rajdhani">
              About
            </a>
            <a href="#contact" className="text-slate-300 hover:text-saturn-orange transition-colors font-rajdhani">
              Contact
            </a>
            
            {user ? (
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="ghost"
                  className="text-slate-300 hover:text-saturn-orange"
                  data-testid="nav-dashboard-btn"
                >
                  Dashboard
                </Button>
                <Button
                  onClick={logout}
                  variant="ghost"
                  className="text-slate-300 hover:text-saturn-orange"
                  data-testid="nav-logout-btn"
                >
                  Logout
                </Button>
              </div>
            ) : null}
            {/* Partner Login moved to footer (see PartnerLoginTrigger) so the
                staff/admin portal is still reachable but not prominent to
                first-time customer visitors. */}
            
            <Button
              onClick={() => navigate('/book')}
              className="bg-saturn-orange hover:bg-saturn-orange-hover text-white font-rajdhani font-bold uppercase tracking-wider px-6 py-2 neon-glow transition-all"
              data-testid="nav-book-now-btn"
            >
              Book Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white p-2"
            data-testid="nav-mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden glass mt-4 rounded-xl overflow-hidden"
            >
              <div className="flex flex-col p-4 gap-4">
                <a href="#services" className="text-slate-300 hover:text-saturn-orange py-2 font-rajdhani">
                  Services
                </a>
                <Link to="/blog" onClick={() => setIsOpen(false)} className="text-slate-300 hover:text-saturn-orange py-2 font-rajdhani" data-testid="nav-mobile-blog-link">
                  Guides
                </Link>
                <a href="#about" className="text-slate-300 hover:text-saturn-orange py-2 font-rajdhani">
                  About
                </a>
                <a href="#contact" className="text-slate-300 hover:text-saturn-orange py-2 font-rajdhani">
                  Contact
                </a>
                {user ? (
                  <>
                    <Button onClick={() => navigate('/dashboard')} variant="ghost" className="justify-start">
                      Dashboard
                    </Button>
                    <Button onClick={logout} variant="ghost" className="justify-start">
                      Logout
                    </Button>
                  </>
                ) : null}
                {/* Partner Login moved to footer — see PartnerLoginTrigger. */}
                <Button
                  onClick={() => navigate('/book')}
                  className="bg-saturn-orange hover:bg-saturn-orange-hover text-white font-rajdhani font-bold w-full"
                >
                  Book Now
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
    </>
  );
};

// Hero Section with Enhanced Scrub-to-Reveal Slider
const HeroSection = () => {
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.3], [1, 0.95]);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [sliderPosition2, setSliderPosition2] = useState(50);
  const [sliderPosition3, setSliderPosition3] = useState(50);
  const sliderRef = useRef(null);
  const sliderRef2 = useRef(null);
  const sliderRef3 = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDragging2, setIsDragging2] = useState(false);
  const [isDragging3, setIsDragging3] = useState(false);
  const [activeSlider, setActiveSlider] = useState("wraps");
  const [isAutoRotating, setIsAutoRotating] = useState(true);

  // Auto-rotate slider tabs (pauses when dragging any slider)
  useEffect(() => {
    // Don't run timer if auto-rotating is off OR if user is dragging any slider
    if (!isAutoRotating || isDragging || isDragging2 || isDragging3) return;
    
    const tabs = ["wraps", "tints", "ceramic"];
    const interval = setInterval(() => {
      setActiveSlider(current => {
        const currentIndex = tabs.indexOf(current);
        const nextIndex = (currentIndex + 1) % tabs.length;
        return tabs[nextIndex];
      });
    }, 5000); // Switch every 5 seconds
    
    return () => clearInterval(interval);
  }, [isAutoRotating, isDragging, isDragging2, isDragging3]);

  // Pause auto-rotation when user interacts with sliders
  const handleTabClick = (tab) => {
    setActiveSlider(tab);
    setIsAutoRotating(false);
    // Resume auto-rotation after 15 seconds of inactivity
    setTimeout(() => setIsAutoRotating(true), 15000);
  };

  // Lock page scroll when any slider is being dragged
  useEffect(() => {
    if (isDragging || isDragging2 || isDragging3) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isDragging, isDragging2, isDragging3]);

  // Custom scrub handler for touch/mouse
  const handleScrub = (clientX) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const position = ((clientX - rect.left) / rect.width) * 100;
    setSliderPosition(Math.min(100, Math.max(0, position)));
  };

  const handleScrub2 = (clientX) => {
    if (!sliderRef2.current) return;
    const rect = sliderRef2.current.getBoundingClientRect();
    const position = ((clientX - rect.left) / rect.width) * 100;
    setSliderPosition2(Math.min(100, Math.max(0, position)));
  };

  const handleScrub3 = (clientX) => {
    if (!sliderRef3.current) return;
    const rect = sliderRef3.current.getBoundingClientRect();
    const position = ((clientX - rect.left) / rect.width) * 100;
    setSliderPosition3(Math.min(100, Math.max(0, position)));
  };

  return (
    <motion.section
      style={{ opacity, scale }}
      className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-hero-gradient" />
      <div className="absolute inset-0 grid-lines opacity-30" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            <h1 className="font-orbitron text-5xl md:text-7xl font-black tracking-tighter uppercase text-white mb-6">
              <span className="block">Transform</span>
              <span className="block text-saturn-orange">Your Ride</span>
            </h1>

            <p className="text-lg md:text-xl text-slate-400 font-inter mb-8 max-w-lg mx-auto lg:mx-0">
              Experience the future of automotive styling with Saturn's precision ceramic 
              tinting technology. Block UV rays, reduce heat, elevate your drive.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                onClick={() => navigate('/book')}
                className="btn-skew bg-saturn-orange hover:bg-saturn-orange-hover text-white font-rajdhani font-bold uppercase tracking-wider px-8 py-4 text-lg neon-glow"
                data-testid="hero-book-btn"
              >
                <span>Book Your Slot</span>
              </Button>
              <Button
                variant="outline"
                className="border-saturn-orange/50 text-saturn-orange hover:bg-saturn-orange/10 font-rajdhani font-medium uppercase tracking-wider px-8 py-4"
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-services-btn"
              >
                View Services
              </Button>
            </div>
            
            {/* Quick Contact Options */}
            <div className="flex flex-wrap gap-3 mt-6 justify-center lg:justify-start">
              <a href={`tel:${PHONE_NUMBER}`}>
                <Button
                  variant="ghost"
                  className="text-slate-300 hover:text-white hover:bg-slate-800 font-rajdhani"
                  data-testid="hero-call-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call Us
                </Button>
              </a>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                <Button
                  variant="ghost"
                  className="text-green-400 hover:text-green-300 hover:bg-green-900/20 font-rajdhani"
                  data-testid="hero-whatsapp-btn"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </Button>
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-12">
              {[
                { value: "99%", label: "UV Blocked" },
                { value: "50%", label: "Heat Reduction" },
                { value: "5yr", label: "Warranty" },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                  className="text-center"
                >
                  <div className="font-orbitron text-3xl md:text-4xl font-bold text-saturn-orange">
                    {stat.value}
                  </div>
                  <div className="text-sm text-slate-400 font-rajdhani uppercase tracking-wider">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Enhanced Scrub-to-Reveal Sliders */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            {/* Auto-rotation indicator */}
            <div className="flex gap-1 mb-3">
              {["wraps", "tints", "ceramic"].map((tab) => (
                <div
                  key={tab}
                  className="h-1 rounded-full bg-slate-700 overflow-hidden flex-1"
                >
                  {activeSlider === tab && isAutoRotating && (
                    <motion.div
                      className="h-full bg-saturn-orange"
                      initial={{ width: '0%' }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 5, ease: 'linear' }}
                      key={`progress-${activeSlider}`}
                    />
                  )}
                  {activeSlider === tab && !isAutoRotating && (
                    <div className="h-full bg-saturn-orange w-full" />
                  )}
                </div>
              ))}
            </div>

            {/* Tints Slider - 20% larger */}
            <div className={`relative rounded-2xl overflow-hidden glass p-2 ${activeSlider !== "tints" ? "hidden" : ""}`}>
              <div 
                ref={sliderRef}
                className="relative rounded-xl overflow-hidden cursor-ew-resize select-none" style={{ aspectRatio: '16/11' }}
                onMouseDown={(e) => {
                  setIsDragging(true);
                  handleScrub(e.clientX);
                }}
                onMouseMove={(e) => isDragging && handleScrub(e.clientX)}
                onMouseUp={() => setIsDragging(false)}
                onMouseLeave={() => setIsDragging(false)}
                onTouchStart={(e) => {
                  setIsDragging(true);
                  handleScrub(e.touches[0].clientX);
                }}
                onTouchMove={(e) => isDragging && handleScrub(e.touches[0].clientX)}
                onTouchEnd={() => setIsDragging(false)}
              >
                {/* Before Image - Tesla without tints */}
                <img
                  src="https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/433fy4cg_20240423_162900.jpg"
                  alt="Before - No tints"
                  className="absolute inset-0 w-full h-full object-cover"
                  draggable={false}
                />
                
                {/* After Image - Tesla with tints */}
                <div 
                  className="absolute inset-0 overflow-hidden"
                  style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/qrg412ox_20240423_190117.jpg"
                    alt="After - Tinted"
                    className="absolute inset-0 w-full h-full object-cover"
                    draggable={false}
                  />
                </div>

                {/* Slider Handle - 50% smaller, positioned at bottom */}
                <div 
                  className="absolute top-0 bottom-0 w-1 bg-saturn-orange z-10 transition-none"
                  style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
                >
                  <motion.div 
                    className="absolute bottom-6 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-saturn-orange flex items-center justify-center shadow-lg border-2 border-white"
                    animate={{ 
                      scale: isDragging ? [1, 1.15, 1.1] : 1,
                      boxShadow: isDragging 
                        ? "0 0 20px rgba(249, 115, 22, 0.6), 0 0 40px rgba(249, 115, 22, 0.3)" 
                        : "0 4px 6px rgba(0, 0, 0, 0.1)"
                    }}
                    transition={{ 
                      scale: { duration: 0.2, times: [0, 0.6, 1] },
                      boxShadow: { duration: 0.2 }
                    }}
                    whileHover={{ scale: 1.1 }}
                  >
                    <div className="flex items-center gap-0">
                      <ChevronRight className="w-2 h-2 text-white rotate-180" />
                      <ChevronRight className="w-2 h-2 text-white" />
                    </div>
                  </motion.div>
                  {/* Haptic feedback ring */}
                  <AnimatePresence>
                    {isDragging && (
                      <motion.div
                        className="absolute bottom-6 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full border-2 border-saturn-orange"
                        initial={{ scale: 1, opacity: 0.8 }}
                        animate={{ scale: 1.8, opacity: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                      />
                    )}
                  </AnimatePresence>
                </div>

                {/* Scrub Hint */}
                <AnimatePresence>
                  {sliderPosition === 50 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                      <motion.div
                        animate={{ x: [-20, 20, -20] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="px-4 py-2 rounded-full glass text-sm font-rajdhani text-white"
                      >
                        Drag to reveal
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Labels */}
              <div className="absolute bottom-6 left-6 px-3 py-1 rounded-full bg-saturn-orange text-white text-sm font-rajdhani z-20">
                After
              </div>
              <div className="absolute bottom-6 right-6 px-3 py-1 rounded-full glass text-sm font-rajdhani z-20">
                Before
              </div>
            </div>

            {/* Wraps Slider - 20% larger */}
            <div className={`relative rounded-2xl overflow-hidden glass p-2 ${activeSlider !== "wraps" ? "hidden" : ""}`}>
              <div 
                ref={sliderRef2}
                className="relative rounded-xl overflow-hidden cursor-ew-resize select-none" style={{ aspectRatio: '16/11' }}
                onMouseDown={(e) => {
                  setIsDragging2(true);
                  handleScrub2(e.clientX);
                }}
                onMouseMove={(e) => isDragging2 && handleScrub2(e.clientX)}
                onMouseUp={() => setIsDragging2(false)}
                onMouseLeave={() => setIsDragging2(false)}
                onTouchStart={(e) => {
                  setIsDragging2(true);
                  handleScrub2(e.touches[0].clientX);
                }}
                onTouchMove={(e) => isDragging2 && handleScrub2(e.touches[0].clientX)}
                onTouchEnd={() => setIsDragging2(false)}
              >
                {/* Before Image - White BMW */}
                <img
                  src="https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/ovn7hqo1_20230628_184906.jpg"
                  alt="Before - White BMW"
                  className="absolute inset-0 w-full h-full object-cover"
                  draggable={false}
                />
                
                {/* After Image - Green Wrapped BMW */}
                <div 
                  className="absolute inset-0 overflow-hidden"
                  style={{ clipPath: `inset(0 ${100 - sliderPosition2}% 0 0)` }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/0b2vajdu_20230704_183353.jpg"
                    alt="After - Wrapped BMW"
                    className="absolute inset-0 w-full h-full object-cover"
                    draggable={false}
                  />
                </div>

                {/* Slider Handle - 50% smaller, positioned at bottom */}
                <div 
                  className="absolute top-0 bottom-0 w-1 bg-saturn-orange z-10 transition-none"
                  style={{ left: `${sliderPosition2}%`, transform: 'translateX(-50%)' }}
                >
                  <motion.div 
                    className="absolute bottom-6 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-saturn-orange flex items-center justify-center shadow-lg border-2 border-white"
                    animate={{ 
                      scale: isDragging2 ? [1, 1.15, 1.1] : 1,
                      boxShadow: isDragging2 
                        ? "0 0 20px rgba(249, 115, 22, 0.6), 0 0 40px rgba(249, 115, 22, 0.3)" 
                        : "0 4px 6px rgba(0, 0, 0, 0.1)"
                    }}
                    transition={{ 
                      scale: { duration: 0.2, times: [0, 0.6, 1] },
                      boxShadow: { duration: 0.2 }
                    }}
                    whileHover={{ scale: 1.1 }}
                  >
                    <div className="flex items-center gap-0">
                      <ChevronRight className="w-2 h-2 text-white rotate-180" />
                      <ChevronRight className="w-2 h-2 text-white" />
                    </div>
                  </motion.div>
                  {/* Haptic feedback ring */}
                  <AnimatePresence>
                    {isDragging2 && (
                      <motion.div
                        className="absolute bottom-6 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full border-2 border-saturn-orange"
                        initial={{ scale: 1, opacity: 0.8 }}
                        animate={{ scale: 1.8, opacity: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                      />
                    )}
                  </AnimatePresence>
                </div>

                {/* Scrub Hint */}
                <AnimatePresence>
                  {sliderPosition2 === 50 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                      <motion.div
                        animate={{ x: [-20, 20, -20] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="px-4 py-2 rounded-full glass text-sm font-rajdhani text-white"
                      >
                        Drag to reveal
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Labels */}
              <div className="absolute bottom-6 left-6 px-3 py-1 rounded-full bg-saturn-orange text-white text-sm font-rajdhani z-20">
                After
              </div>
              <div className="absolute bottom-6 right-6 px-3 py-1 rounded-full glass text-sm font-rajdhani z-20">
                Before
              </div>
            </div>

            {/* Paint Correction Slider - 20% larger */}
            <div className={`relative rounded-2xl overflow-hidden glass p-2 ${activeSlider !== "ceramic" ? "hidden" : ""}`}>
              <div 
                ref={sliderRef3}
                className="relative rounded-xl overflow-hidden cursor-ew-resize select-none" style={{ aspectRatio: '16/11' }}
                onMouseDown={(e) => {
                  setIsDragging3(true);
                  handleScrub3(e.clientX);
                }}
                onMouseMove={(e) => isDragging3 && handleScrub3(e.clientX)}
                onMouseUp={() => setIsDragging3(false)}
                onMouseLeave={() => setIsDragging3(false)}
                onTouchStart={(e) => {
                  setIsDragging3(true);
                  handleScrub3(e.touches[0].clientX);
                }}
                onTouchMove={(e) => isDragging3 && handleScrub3(e.touches[0].clientX)}
                onTouchEnd={() => setIsDragging3(false)}
              >
                {/* Before Image - BMW X5 before buffing */}
                <img
                  src="https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/rgkrlfi7_20240322_114640.jpg"
                  alt="Before - Dull paint"
                  className="absolute inset-0 w-full h-full object-cover"
                  draggable={false}
                />
                
                {/* After Image - BMW X5 after buffing + ceramic */}
                <div 
                  className="absolute inset-0 overflow-hidden"
                  style={{ clipPath: `inset(0 ${100 - sliderPosition3}% 0 0)` }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/da17me8f_20240325_142924.jpg"
                    alt="After - Ceramic coated"
                    className="absolute inset-0 w-full h-full object-cover"
                    draggable={false}
                  />
                </div>

                {/* Slider Handle - 50% smaller, positioned at bottom */}
                <div 
                  className="absolute top-0 bottom-0 w-1 bg-saturn-orange z-10 transition-none"
                  style={{ left: `${sliderPosition3}%`, transform: 'translateX(-50%)' }}
                >
                  <motion.div 
                    className="absolute bottom-6 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-saturn-orange flex items-center justify-center shadow-lg border-2 border-white"
                    animate={{ 
                      scale: isDragging3 ? [1, 1.15, 1.1] : 1,
                      boxShadow: isDragging3 
                        ? "0 0 20px rgba(249, 115, 22, 0.6), 0 0 40px rgba(249, 115, 22, 0.3)" 
                        : "0 4px 6px rgba(0, 0, 0, 0.1)"
                    }}
                    transition={{ 
                      scale: { duration: 0.2, times: [0, 0.6, 1] },
                      boxShadow: { duration: 0.2 }
                    }}
                    whileHover={{ scale: 1.1 }}
                  >
                    <div className="flex items-center gap-0">
                      <ChevronRight className="w-2 h-2 text-white rotate-180" />
                      <ChevronRight className="w-2 h-2 text-white" />
                    </div>
                  </motion.div>
                  {/* Haptic feedback ring */}
                  <AnimatePresence>
                    {isDragging3 && (
                      <motion.div
                        className="absolute bottom-6 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full border-2 border-saturn-orange"
                        initial={{ scale: 1, opacity: 0.8 }}
                        animate={{ scale: 1.8, opacity: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                      />
                    )}
                  </AnimatePresence>
                </div>

                {/* Scrub Hint */}
                <AnimatePresence>
                  {sliderPosition3 === 50 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                      <motion.div
                        animate={{ x: [-20, 20, -20] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="px-4 py-2 rounded-full glass text-sm font-rajdhani text-white"
                      >
                        Drag to reveal
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Labels */}
              <div className="absolute bottom-6 left-6 px-3 py-1 rounded-full bg-saturn-orange text-white text-sm font-rajdhani z-20">
                After
              </div>
              <div className="absolute bottom-6 right-6 px-3 py-1 rounded-full glass text-sm font-rajdhani z-20">
                Before
              </div>
            </div>

            {/* Slider Tabs - Bottom Center, 50% smaller */}
            <div className="flex justify-center gap-1.5 mt-3">
              <button
                onClick={() => handleTabClick("wraps")}
                className={`px-3 py-1 rounded-full font-rajdhani text-xs uppercase tracking-wider transition-all ${
                  activeSlider === "wraps" 
                    ? "bg-saturn-orange text-white" 
                    : "glass text-slate-400 hover:text-white"
                }`}
              >
                Wraps
              </button>
              <button
                onClick={() => handleTabClick("tints")}
                className={`px-3 py-1 rounded-full font-rajdhani text-xs uppercase tracking-wider transition-all ${
                  activeSlider === "tints" 
                    ? "bg-saturn-orange text-white" 
                    : "glass text-slate-400 hover:text-white"
                }`}
              >
                Tints
              </button>
              <button
                onClick={() => handleTabClick("ceramic")}
                className={`px-3 py-1 rounded-full font-rajdhani text-xs uppercase tracking-wider transition-all ${
                  activeSlider === "ceramic" 
                    ? "bg-saturn-orange text-white" 
                    : "glass text-slate-400 hover:text-white"
                }`}
              >
                Paint
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 rounded-full border-2 border-slate-600 flex items-start justify-center p-2">
          <div className="w-1.5 h-2.5 rounded-full bg-saturn-orange" />
        </div>
      </motion.div>
    </motion.section>
  );
};

// Services Section with Scroll Animations
const ServicesSection = ({ onOpenQuote }) => {
  const navigate = useNavigate();
  
  const services = [
    {
      icon: Car,
      title: "Window Tinting",
      description: "Professional ceramic window tinting for all vehicle types",
      price: "From €150",
      features: ["Performance", "Savvy Ceramic", "Chameleon", "5yr Warranty"],
      category: "tinting",
    },
    {
      icon: Shield,
      title: "Ceramic Coating",
      description: "Long-lasting paint protection with hydrophobic properties",
      price: "€300",
      features: ["Paint Protection", "Hydrophobic", "UV Resistant", "Multi-day job"],
      category: "coating",
    },
    {
      icon: Sparkles,
      title: "Vehicle Wrapping",
      description: "Transform your vehicle with premium vinyl wraps",
      price: "Custom quote",
      features: ["Full Wrap", "Roof Wrap", "Chrome Delete", "Custom Colors"],
      category: "wrapping",
    },
    {
      icon: Droplets,
      title: "Paint Correction",
      description: "Restore your paint to showroom condition",
      price: "From €250",
      features: ["Stage 1 (1 day)", "Stage 2 (2 days)", "Stage 3 (3 days)", "Swirl Removal"],
      category: "polishing",
    },
  ];

  const handleServiceClick = (service) => {
    if (service.hasQuote) {
      onOpenQuote();
    } else {
      navigate('/book', { state: { preselectedCategory: service.category } });
    }
  };

  return (
    <section id="services" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-saturn-slate/50 to-transparent" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-16">
            <span className="text-saturn-orange font-rajdhani uppercase tracking-widest text-sm">
              Our Services
            </span>
            <h2 className="font-orbitron text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mt-4">
              Premium Automotive Styling
            </h2>
            <p className="text-slate-400 mt-4 max-w-2xl mx-auto font-inter">
              Click on any service to start your booking
            </p>
          </div>
        </ScrollReveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <ScrollReveal key={index} delay={index * 0.1}>
              <motion.div
                onClick={() => handleServiceClick(service)}
                className="glass glass-hover rounded-xl p-6 group cursor-pointer hover:border-saturn-orange transition-all h-full"
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                data-testid={`service-card-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-saturn-orange/20 flex items-center justify-center mb-4 group-hover:bg-saturn-orange/30 transition-colors">
                  <service.icon className="w-6 h-6 text-saturn-orange" />
                </div>
                
                <h3 className="font-rajdhani font-bold text-xl text-white mb-2">
                  {service.title}
                </h3>
                <p className="text-slate-400 text-sm mb-4 font-inter">
                  {service.description}
                </p>
                
                <div className="font-orbitron text-2xl text-saturn-orange mb-4">
                  {service.price}
                </div>
                
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-saturn-orange" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <div className="mt-4 pt-4 border-t border-slate-700 text-center">
                  {service.hasQuote ? (
                    <span className="text-saturn-orange text-sm font-rajdhani group-hover:underline flex items-center justify-center gap-1">
                      <MessageSquare className="w-4 h-4" />
                      Get Quote →
                    </span>
                  ) : (
                    <span className="text-saturn-orange text-sm font-rajdhani group-hover:underline">
                      Book Now →
                    </span>
                  )}
                </div>
              </motion.div>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal delay={0.4}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
            <Link to="/book">
              <Button
                className="bg-saturn-orange hover:bg-saturn-orange-hover text-white font-rajdhani font-bold uppercase tracking-wider px-8 py-4 text-lg neon-glow"
                data-testid="services-book-btn"
              >
                Book Your Service <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={onOpenQuote}
              className="border-saturn-orange/50 text-saturn-orange hover:bg-saturn-orange/10 font-rajdhani font-medium uppercase tracking-wider px-8 py-4"
              data-testid="services-quote-btn"
            >
              <MessageSquare className="mr-2 w-5 h-5" />
              Request a Quote
            </Button>
          </div>
          
          {/* Call/WhatsApp CTA */}
          <div className="text-center mt-8">
            <p className="text-slate-400 text-sm mb-3 font-inter">Questions? We're here to help</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a href={`tel:${PHONE_NUMBER}`}>
                <Button
                  variant="ghost"
                  className="text-slate-300 hover:text-white hover:bg-slate-800 font-rajdhani border border-slate-700"
                  data-testid="services-call-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  085 823 7373
                </Button>
              </a>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                <Button
                  variant="ghost"
                  className="text-green-400 hover:text-green-300 hover:bg-green-900/20 font-rajdhani border border-green-800"
                  data-testid="services-whatsapp-btn"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Chat on WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

// Tint Shade Interactive Visualizer
const TintShadeVisualizer = () => {
  const [selectedShade, setSelectedShade] = useState(20);
  
  const tintShades = [
    { vlt: 5, name: "Limo", description: "Maximum privacy, darkest legal tint for rear windows", dayOpacity: 0.85, nightOpacity: 0.95 },
    { vlt: 20, name: "Comfort", description: "Popular choice - great privacy with good visibility", dayOpacity: 0.65, nightOpacity: 0.85 },
    { vlt: 30, name: "Light", description: "Balanced - moderate privacy, excellent visibility", dayOpacity: 0.50, nightOpacity: 0.75 },
    { vlt: 40, name: "Very Light", description: "Subtle tint - slight privacy, maximum clarity", dayOpacity: 0.35, nightOpacity: 0.60 },
    { vlt: 65, name: "Road Legal", description: "Front windscreen & front side windows — Irish RSA legal minimum (65% VLT)", dayOpacity: 0.20, nightOpacity: 0.40 },
    { vlt: 100, name: "Clear Glass", description: "No tint - factory clear glass reference", dayOpacity: 0, nightOpacity: 0 },
  ];

  const currentShade = tintShades.find(s => s.vlt === selectedShade) || tintShades[1];

  return (
    <section className="py-20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-12">
            <h2 className="font-orbitron text-3xl md:text-4xl font-bold text-white mb-4">
              Tint Shade <span className="text-saturn-orange">Visualizer</span>
            </h2>
            <p className="text-slate-400 font-inter max-w-2xl mx-auto">
              See how different VLT (Visible Light Transmission) percentages affect visibility during day and night
            </p>
          </div>
        </ScrollReveal>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Day & Night Window Displays */}
          <ScrollReveal delay={0.2}>
            <div className="space-y-4">
              {/* Day View */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <Sun className="w-5 h-5 text-yellow-400" />
                  <span className="font-rajdhani font-bold text-white">Daytime View</span>
                  <span className="text-slate-400 text-sm">- More light, clearer visibility</span>
                </div>
                <div className="relative aspect-[16/9] rounded-xl overflow-hidden glass border-2 border-slate-700">
                  <img 
                    src="https://images.unsplash.com/photo-1759759263415-3d149700767c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxJcmVsYW5kJTIwcm9hZCUyMGRyaXZpbmclMjBncmVlbiUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3NzI3MzA4NTV8MA&ixlib=rb-4.1.0&q=85"
                    alt="Irish countryside road - day view"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <motion.div
                    className="absolute inset-0 bg-black"
                    animate={{ opacity: currentShade.dayOpacity }}
                    transition={{ duration: 0.5 }}
                  />
                  <div className="absolute inset-0 border-8 border-slate-800 rounded-lg pointer-events-none" />
                  <div className="absolute top-3 left-3 px-2 py-1 rounded bg-saturn-black/80 backdrop-blur-sm">
                    <span className="text-saturn-orange font-orbitron text-lg font-bold">{selectedShade}% VLT</span>
                  </div>
                  
                  {/* Compact VLT Selector Bar - Bottom of Day Photo */}
                  <div className="absolute bottom-3 left-3 right-3 flex justify-center gap-1">
                    {tintShades.map((shade) => (
                      <div key={shade.vlt} className="relative group">
                        <motion.button
                          onClick={() => setSelectedShade(shade.vlt)}
                          className={`w-10 h-8 rounded-md flex items-center justify-center text-xs font-bold transition-all ${
                            selectedShade === shade.vlt
                              ? 'bg-saturn-orange text-white shadow-lg shadow-saturn-orange/30'
                              : 'bg-saturn-black/80 text-slate-300 hover:bg-slate-700 backdrop-blur-sm'
                          }`}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {shade.vlt}%
                        </motion.button>
                        {/* Hover Tooltip */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 rounded-lg bg-saturn-black/95 border border-slate-600 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-20 pointer-events-none">
                          <p className="text-saturn-orange font-orbitron text-sm font-bold">{shade.name}</p>
                          <p className="text-slate-300 text-xs mt-1">{shade.description}</p>
                          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-600"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Night View */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-blue-400" />
                  <span className="font-rajdhani font-bold text-white">Nighttime View</span>
                  <span className="text-slate-400 text-sm">- Less light, reduced visibility</span>
                </div>
                <div className="relative aspect-[16/9] rounded-xl overflow-hidden glass border-2 border-slate-700">
                  <img 
                    src="https://images.unsplash.com/photo-1765300013135-e047e42de2ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxuaWdodCUyMHJvYWQlMjBkcml2aW5nJTIwc3RyZWV0JTIwbGlnaHRzJTIwZGFya3xlbnwwfHx8fDE3NzI3MzEwMjV8MA&ixlib=rb-4.1.0&q=85"
                    alt="Night road with street lights"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <motion.div
                    className="absolute inset-0 bg-black"
                    animate={{ opacity: currentShade.nightOpacity }}
                    transition={{ duration: 0.5 }}
                  />
                  <div className="absolute inset-0 border-8 border-slate-800 rounded-lg pointer-events-none" />
                  <div className="absolute top-3 left-3 px-2 py-1 rounded bg-saturn-black/80 backdrop-blur-sm">
                    <span className="text-saturn-orange font-orbitron text-lg font-bold">{selectedShade}% VLT</span>
                  </div>
                  
                  {/* Compact VLT Selector Bar - Bottom of Night Photo */}
                  <div className="absolute bottom-3 left-3 right-3 flex justify-center gap-1">
                    {tintShades.map((shade) => (
                      <div key={shade.vlt} className="relative group">
                        <motion.button
                          onClick={() => setSelectedShade(shade.vlt)}
                          className={`w-10 h-8 rounded-md flex items-center justify-center text-xs font-bold transition-all ${
                            selectedShade === shade.vlt
                              ? 'bg-saturn-orange text-white shadow-lg shadow-saturn-orange/30'
                              : 'bg-saturn-black/80 text-slate-300 hover:bg-slate-700 backdrop-blur-sm'
                          }`}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {shade.vlt}%
                        </motion.button>
                        {/* Hover Tooltip */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 rounded-lg bg-saturn-black/95 border border-slate-600 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-20 pointer-events-none">
                          <p className="text-saturn-orange font-orbitron text-sm font-bold">{shade.name}</p>
                          <p className="text-slate-300 text-xs mt-1">{shade.description}</p>
                          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-600"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Visibility comparison note */}
              <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                <p className="text-slate-300 text-sm font-inter">
                  <span className="text-saturn-orange font-bold">Note:</span> Darker tints significantly reduce night visibility. 
                  Choose wisely based on your typical driving conditions.
                </p>
              </div>
            </div>
          </ScrollReveal>

          {/* Shade Selection */}
          <ScrollReveal delay={0.3}>
            <div className="space-y-3">
              {tintShades.map((shade) => (
                <motion.button
                  key={shade.vlt}
                  onClick={() => setSelectedShade(shade.vlt)}
                  className={`w-full p-4 rounded-xl text-left transition-all ${
                    selectedShade === shade.vlt
                      ? 'glass border-saturn-orange bg-saturn-orange/10'
                      : 'glass hover:border-slate-600'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center gap-4">
                    {/* Day/Night Preview Swatches */}
                    <div className="flex gap-1 flex-shrink-0">
                      <div 
                        className="w-10 h-10 rounded-lg border border-slate-600 relative overflow-hidden"
                        title="Day view"
                      >
                        <img 
                          src="https://images.unsplash.com/photo-1759759263415-3d149700767c?crop=entropy&cs=srgb&fm=jpg&w=100&q=60"
                          alt="Day"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div 
                          className="absolute inset-0 bg-black transition-opacity duration-300"
                          style={{ opacity: shade.dayOpacity }}
                        />
                      </div>
                      <div 
                        className="w-10 h-10 rounded-lg border border-slate-600 relative overflow-hidden"
                        title="Night view"
                      >
                        <img 
                          src="https://images.unsplash.com/photo-1765300013135-e047e42de2ea?crop=entropy&cs=srgb&fm=jpg&w=100&q=60"
                          alt="Night"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div 
                          className="absolute inset-0 bg-black transition-opacity duration-300"
                          style={{ opacity: shade.nightOpacity }}
                        />
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-orbitron font-bold text-white">
                          {shade.vlt}% VLT
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-rajdhani ${
                          shade.vlt === 65 
                            ? 'bg-green-500/20 text-green-400' 
                            : shade.vlt === 5 
                            ? 'bg-purple-500/20 text-purple-400'
                            : 'bg-slate-700 text-slate-300'
                        }`}>
                          {shade.name}
                        </span>
                        {shade.vlt === 20 && (
                          <span className="px-2 py-0.5 bg-saturn-orange/20 text-saturn-orange text-xs rounded font-rajdhani">
                            POPULAR
                          </span>
                        )}
                      </div>
                      <p className="text-slate-400 text-sm mt-1">{shade.description}</p>
                    </div>
                    
                    {/* Selection indicator */}
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      selectedShade === shade.vlt
                        ? 'border-saturn-orange bg-saturn-orange'
                        : 'border-slate-600'
                    }`}>
                      {selectedShade === shade.vlt && (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      )}
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Legal Notice */}
            <div className="mt-6 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-yellow-400 font-rajdhani font-bold text-sm">Irish Road Traffic Laws</p>
                  <p className="text-slate-400 text-sm mt-1">
                    Front windscreen and front side windows must allow 65% VLT minimum. Rear windows have no legal restrictions.
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

// Recent Jobs Gallery Section
const RecentJobsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  const recentJobs = [
    {
      image: "https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/xzf7sxvl_20250820_172627.jpg",
      vehicle: "Porsche Taycan",
      service: "Full Car Ceramic Tint",
      description: "Savvy Ceramic package with 5-year warranty"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/x4bqae6z_20250816_124902.jpg",
      vehicle: "Tesla Model S",
      service: "Rear Section Ceramic Tints",
      description: "Savvy Ceramic package with 5-year warranty"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/bty5l8a5_20250726_120141.jpg",
      vehicle: "BMW i4",
      service: "Rear Section Ceramic Tint",
      description: "Savvy Ceramic package with 5-year warranty"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/vr5ow8xw_20241221_155525.jpg",
      vehicle: "Porsche Cayenne",
      service: "Stage 2 Buffing + Ceramic Coating",
      description: "Coarse machine buffing with ceramic paint protection"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/8pxivc8h_20241121_102424.jpg",
      vehicle: "Porsche 911 Targa 4 GTS",
      service: "Stage 2 Buffing + Ceramic Coating",
      description: "Coarse machine buffing with ceramic paint protection"
    }
  ];

  // Auto-scroll effect
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % recentJobs.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [isAutoPlaying, recentJobs.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % recentJobs.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + recentJobs.length) % recentJobs.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <section id="recent-jobs" className="py-20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-12">
            <h2 className="font-orbitron text-3xl md:text-4xl font-bold text-white mb-4">
              Recent <span className="text-saturn-orange">Jobs</span>
            </h2>
            <p className="text-slate-400 font-inter max-w-2xl mx-auto">
              Check out some of our latest work. Every vehicle leaves our bay looking stunning.
            </p>
          </div>
        </ScrollReveal>

        {/* Main Carousel */}
        <div className="relative">
          {/* Main Image Display */}
          <div 
            className="relative aspect-[16/9] md:aspect-[21/9] rounded-2xl overflow-hidden glass"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0"
              >
                <img
                  src={recentJobs[currentIndex].image}
                  alt={recentJobs[currentIndex].vehicle}
                  className="w-full h-full object-cover"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-saturn-black via-transparent to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-saturn-black/50 via-transparent to-saturn-black/50" />
              </motion.div>
            </AnimatePresence>

            {/* Job Info Overlay */}
            <motion.div 
              className="absolute bottom-0 left-0 right-0 p-6 md:p-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              key={`info-${currentIndex}`}
            >
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                  <span className="px-3 py-1 bg-saturn-orange/20 text-saturn-orange text-sm font-rajdhani rounded-full">
                    {recentJobs[currentIndex].service}
                  </span>
                  <h3 className="font-orbitron text-2xl md:text-3xl font-bold text-white mt-2">
                    {recentJobs[currentIndex].vehicle}
                  </h3>
                  <p className="text-slate-300 font-inter mt-1">
                    {recentJobs[currentIndex].description}
                  </p>
                </div>
                <Link to="/book">
                  <Button className="bg-saturn-orange hover:bg-saturn-orange-hover text-white font-rajdhani font-bold whitespace-nowrap">
                    Book Similar Service
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full glass flex items-center justify-center text-white hover:bg-saturn-orange/20 transition-colors"
              aria-label="Previous slide"
            >
              <ChevronRight className="w-6 h-6 rotate-180" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full glass flex items-center justify-center text-white hover:bg-saturn-orange/20 transition-colors"
              aria-label="Next slide"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Thumbnail Navigation */}
          <div className="flex justify-center gap-3 mt-6">
            {recentJobs.map((job, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`relative w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden transition-all ${
                  index === currentIndex 
                    ? 'ring-2 ring-saturn-orange scale-110' 
                    : 'opacity-50 hover:opacity-80'
                }`}
              >
                <img
                  src={job.image}
                  alt={job.vehicle}
                  className="w-full h-full object-cover"
                />
                {index === currentIndex && (
                  <motion.div
                    className="absolute inset-0 bg-saturn-orange/20"
                    layoutId="activeThumb"
                  />
                )}
              </button>
            ))}
          </div>

          {/* Progress Bar */}
          <div className="flex justify-center gap-2 mt-4">
            {recentJobs.map((_, index) => (
              <div
                key={index}
                className="h-1 rounded-full bg-slate-700 overflow-hidden"
                style={{ width: '60px' }}
              >
                {index === currentIndex && isAutoPlaying && (
                  <motion.div
                    className="h-full bg-saturn-orange"
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 4, ease: 'linear' }}
                    key={`progress-${currentIndex}`}
                  />
                )}
                {index === currentIndex && !isAutoPlaying && (
                  <div className="h-full bg-saturn-orange w-full" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Benefits Section with Scroll-Triggered Animations (Savvy Ceramic vs Performance)
const BenefitsSection = () => {
  const benefits = [
    {
      title: "Savvy Ceramic Tinting",
      icon: Shield,
      color: "saturn-orange",
      points: [
        { label: "Heat Rejection", value: "50%+", highlight: true },
        { label: "UV Protection", value: "99%", highlight: true },
        { label: "Signal Interference", value: "None", highlight: true },
        { label: "Warranty", value: "5 Years", highlight: true },
        { label: "Fade Resistance", value: "Excellent" },
      ],
    },
    {
      title: "Performance Tinting",
      icon: Car,
      color: "slate-500",
      points: [
        { label: "Heat Rejection", value: "20-30%" },
        { label: "UV Protection", value: "95%" },
        { label: "Signal Interference", value: "None" },
        { label: "Warranty", value: "1 Year" },
        { label: "Fade Resistance", value: "Good" },
      ],
    },
  ];

  return (
    <section className="py-24 md:py-32 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <motion.div
          animate={{
            backgroundPosition: ["0% 0%", "100% 100%"],
          }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 opacity-10"
          style={{
            background: "radial-gradient(circle at center, #F97316 0%, transparent 50%)",
            backgroundSize: "200% 200%",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-16">
            <span className="text-saturn-orange font-rajdhani uppercase tracking-widest text-sm">
              Why Choose Saturn
            </span>
            <h2 className="font-orbitron text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mt-4">
              Savvy Ceramic vs Performance
            </h2>
            <p className="text-slate-400 mt-4 max-w-2xl mx-auto font-inter">
              See why our Savvy Ceramic film outperforms our Performance tier
            </p>
          </div>
        </ScrollReveal>

        <div className="grid md:grid-cols-2 gap-8">
          {benefits.map((benefit, index) => (
            <ScrollReveal key={index} delay={index * 0.2} direction={index === 0 ? "left" : "right"}>
              <motion.div
                className={`relative rounded-2xl p-8 ${
                  index === 0 
                    ? "glass border-saturn-orange/50" 
                    : "bg-slate-800/30 border border-slate-700"
                }`}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                {index === 0 && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 rounded-full bg-saturn-orange text-white text-xs font-rajdhani uppercase tracking-wider">
                      Recommended
                    </span>
                  </div>
                )}
                
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 rounded-xl ${
                    index === 0 ? "bg-saturn-orange/20" : "bg-slate-700"
                  } flex items-center justify-center`}>
                    <benefit.icon className={`w-7 h-7 ${
                      index === 0 ? "text-saturn-orange" : "text-slate-400"
                    }`} />
                  </div>
                  <h3 className="font-orbitron text-2xl font-bold text-white">
                    {benefit.title}
                  </h3>
                </div>

                <div className="space-y-4">
                  {benefit.points.map((point, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="flex items-center justify-between py-3 border-b border-slate-700/50"
                    >
                      <span className="text-slate-300 font-rajdhani">{point.label}</span>
                      <span className={`font-orbitron font-bold ${
                        point.highlight ? "text-saturn-orange text-lg" : "text-slate-400"
                      }`}>
                        {point.value}
                      </span>
                    </motion.div>
                  ))}
                </div>

                {index === 0 && (
                  <div className="mt-6 p-4 rounded-lg bg-saturn-orange/10 border border-saturn-orange/30">
                    <div className="flex items-center gap-2 text-saturn-orange">
                      <Sparkles className="w-5 h-5" />
                      <span className="font-rajdhani font-bold">FREE Carwash with rear/full tints!</span>
                    </div>
                  </div>
                )}
              </motion.div>
            </ScrollReveal>
          ))}
        </div>

        {/* Animated Stats Bar */}
        <ScrollReveal delay={0.4}>
          <div className="mt-16 glass rounded-2xl p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { icon: Sun, value: "99%", label: "UV Protection" },
                { icon: Thermometer, value: "50%", label: "Heat Blocked" },
                { icon: Shield, value: "5yr", label: "Warranty" },
                {
                  icon: Clock,
                  value: `${yearsInBusiness()}+`,
                  label: "Years in Business",
                },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  className="text-center"
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, type: "spring" }}
                >
                  <stat.icon className="w-8 h-8 text-saturn-orange mx-auto mb-2" />
                  <div className="font-orbitron text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-slate-400 text-sm font-rajdhani">{stat.label}</div>
                </motion.div>
              ))}
            </div>
            
            {/* CTA within stats */}
            <div className="mt-8 pt-6 border-t border-slate-700 text-center">
              <p className="text-slate-400 text-sm mb-4 font-inter">Ready to upgrade your ride? Talk to our experts</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <a href={`tel:${PHONE_NUMBER}`}>
                  <Button
                    className="bg-saturn-orange hover:bg-saturn-orange-hover text-white font-rajdhani font-bold uppercase tracking-wider"
                    data-testid="benefits-call-btn"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call 085 823 7373
                  </Button>
                </a>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white font-rajdhani font-bold uppercase tracking-wider"
                    data-testid="benefits-whatsapp-btn"
                  >
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp Us
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

// About Section with Parallax
const AboutSection = () => {
  return (
    <section id="about" className="py-24 md:py-32 relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1624115984858-ae2fb35fc6e1?w=1920)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(100px)',
        }}
      />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <ScrollReveal direction="left">
            <div>
              <span className="text-saturn-orange font-rajdhani uppercase tracking-widest text-sm">
                The Saturn Manifesto
              </span>
              <h2 className="font-orbitron text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mt-4 mb-6">
                Space-Age Protection
              </h2>
              
              <div className="space-y-4 text-slate-300 font-inter">
                <p>
                  At SaturnStyling, we believe your vehicle deserves nothing less than 
                  the precision and excellence of space-age technology. Our ceramic tinting 
                  solutions are engineered to deliver unparalleled protection against the 
                  elements.
                </p>
                <p>
                  Founded in County Meath, we've built our reputation on quality craftsmanship, 
                  premium materials, and customer satisfaction. Every installation goes through 
                  our rigorous "Saturn 360" inspection process.
                </p>
                <p>
                  Whether you're looking to enhance privacy, reduce heat, or simply give your 
                  vehicle that sleek, premium look — SaturnStyling is your destination for 
                  automotive excellence.
                </p>
                <p>
                  Full VAT invoices are issued for every job, so business and fleet
                  customers can claim the cost straight back through their own accounts.
                </p>
              </div>

              <div className="flex flex-wrap gap-4 mt-8">
                {["Premium Materials", "Expert Installation", "5 Year Warranty", "Free Consultation"].map((item, i) => (
                  <motion.div 
                    key={i} 
                    className="flex items-center gap-2 px-4 py-2 rounded-full glass"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <CheckCircle2 className="w-4 h-4 text-saturn-orange" />
                    <span className="text-sm font-rajdhani text-slate-300">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right">
            <div className="relative">
              <ParallaxImage
                src="https://images.pexels.com/photos/31154212/pexels-photo-31154212.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Saturn Styling Workshop"
                className="rounded-2xl aspect-video"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-saturn-black/80 to-transparent rounded-2xl" />
              <div className="absolute bottom-6 left-6 right-6">
                <GoogleRatingCard />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

// Contact Section with Scroll Animations
const ContactSection = () => {
  return (
    <section id="contact" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 bg-gradient-to-t from-saturn-slate to-transparent" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-16">
            <span className="text-saturn-orange font-rajdhani uppercase tracking-widest text-sm">
              Get In Touch
            </span>
            <h2 className="font-orbitron text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mt-4">
              Visit SaturnStyling
            </h2>
          </div>
        </ScrollReveal>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <ScrollReveal direction="left">
            <div className="space-y-8">
              <motion.div 
                className="glass rounded-xl p-6"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-saturn-orange/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-saturn-orange" />
                  </div>
                  <div>
                    <h3 className="font-rajdhani font-bold text-xl text-white mb-2">Location</h3>
                    <p className="text-slate-300">
                      Unit 4, Platin Industrial Estate<br />
                      Drogheda Road, Duleek<br />
                      Co. Meath, A92 E8WK
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                className="glass rounded-xl p-6"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-saturn-orange/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-saturn-orange" />
                  </div>
                  <div>
                    <h3 className="font-rajdhani font-bold text-xl text-white mb-2">Phone</h3>
                    <a href="tel:+353858237373" className="text-slate-300 hover:text-saturn-orange transition-colors">
                      +353 85 823 7373
                    </a>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                className="glass rounded-xl p-6"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-saturn-orange/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-saturn-orange" />
                  </div>
                  <div>
                    <h3 className="font-rajdhani font-bold text-xl text-white mb-2">Email</h3>
                    <a href="mailto:saturnstyling@gmail.com" className="text-slate-300 hover:text-saturn-orange transition-colors">
                      saturnstyling@gmail.com
                    </a>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                className="glass rounded-xl p-6"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-saturn-orange/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-saturn-orange" />
                  </div>
                  <div>
                    <h3 className="font-rajdhani font-bold text-xl text-white mb-2">Opening Hours</h3>
                    <div className="text-slate-300 space-y-1 text-sm">
                      <p>Mon, Tue, Wed, Fri, Sat: 10am - 6pm</p>
                      <p className="text-blue-400">Twilight Thursday: 12pm - 8pm</p>
                      <p className="text-purple-400">Premium Sunday: 10am - 2pm (+€50)</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </ScrollReveal>

          {/* Map */}
          <ScrollReveal direction="right">
            <div className="glass rounded-2xl overflow-hidden aspect-square lg:aspect-auto lg:h-full">
              <iframe
                src="https://www.google.com/maps?q=SaturnStyling+Duleek+Co+Meath&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '400px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="SaturnStyling Location"
                className="grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-500"
              />
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/zdb6hh5n_Eima%20logo%20Speed%20rate%20font%2020x20.png"
                alt="Saturn Styling"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="font-orbitron font-bold text-lg text-white">
              SATURNSTYLING
            </span>
          </div>
          
          <div className="flex flex-col items-center gap-2">
            <div className="text-slate-400 text-sm font-inter text-center">
              © {new Date().getFullYear()} SaturnStyling. All rights reserved.
            </div>
            <Link to="/terms" className="text-slate-500 hover:text-slate-300 text-xs transition-colors">
              Terms & Conditions
            </Link>
            <Link to="/privacy" className="text-slate-500 hover:text-slate-300 text-xs transition-colors"
                  data-testid="footer-privacy-policy-link">
              Privacy Policy
            </Link>
            <Link to="/promotion-terms" className="text-slate-500 hover:text-slate-300 text-xs transition-colors"
                  data-testid="footer-promotion-terms-link">
              Current Promotion Terms
            </Link>
            <span className="text-slate-500 text-xs" data-testid="footer-vat-invoice-note">
              VAT invoices provided for all services.
            </span>
            <PartnerLoginTrigger />
          </div>
          
          <div className="flex items-center gap-4">
            <a href={`tel:${PHONE_NUMBER}`} className="flex items-center gap-2 text-slate-400 hover:text-saturn-orange transition-colors">
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">085 823 7373</span>
            </a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <span className="hidden sm:inline">WhatsApp</span>
            </a>
            <Link to="/book" className="text-saturn-orange hover:text-saturn-orange-hover transition-colors font-rajdhani font-bold">
              Book Now
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  // Google Ads: intentionally no page-view conversion fire here — the
  // conversion pixel is fired ONLY from BookingSuccessPage.jsx after the
  // backend confirms `status: 'paid'` (see utils/googleAds.js). Prior
  // implementation fired on every landing-page view, polluting the ads
  // account with false conversions.
  //
  // NOTE: LocalBusiness / AutomotiveBusiness JSON-LD used to be injected
  // here at mount via useEffect. It has been moved to `public/index.html`
  // as a STATIC <script type="application/ld+json"> block so every crawler
  // (including non-JS-rendering ones like LinkedIn / Bing / social preview
  // bots) sees it on first HTML fetch. Do not re-add the client injection
  // here — it would duplicate the block in the DOM.

  return (
    <div
      className="min-h-screen bg-saturn-black text-white overflow-x-hidden"
      style={{ paddingTop: "var(--promo-banner-h, 0px)" }}
    >
      <Starfield />
      <PromotionBanner />
      <Navigation />
      <HeroSection />
      <ServicesSection onOpenQuote={() => setQuoteModalOpen(true)} />
      <InstagramFeedSection />
      <TintShadeVisualizer />
      <RecentJobsSection />
      <BenefitsSection />
      <BlogStripSection />
      <AboutSection />
      <ReviewCarousel />
      <ContactSection />
      <Footer />
      
      {/* Floating WhatsApp Button */}
      <FloatingContactButton onClick={() => setContactModalOpen(true)} />
      
      {/* Quote Request Modal */}
      <QuoteRequestModal 
        isOpen={quoteModalOpen} 
        onClose={() => setQuoteModalOpen(false)} 
      />
      
      {/* Contact Options Modal */}
      <ContactPopup
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        title="Contact SaturnStyling"
      />
    </div>
  );
};

export default LandingPage;
